import { inject, Injectable, signal } from '@angular/core';
import {
  collection,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  QueryConstraint,
  DocumentSnapshot,
} from '@angular/fire/firestore';
import { AuditAction, AuditLog } from '../models';
import { AuthStore } from '../auth/auth.store';

export interface AuditLogFilters {
  actorId?: string;
  resourceType?: string;
  resourceId?: string;
  action?: AuditAction;
  startDate?: Date;
  endDate?: Date;
}

export interface PaginatedResult<T> {
  items: T[];
  hasMore: boolean;
  lastDoc: DocumentSnapshot | null;
}

@Injectable({ providedIn: 'root' })
export class AuditService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private readonly _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) {
      throw new Error('No tenant selected');
    }
    return id;
  }

  async getAuditLogs(
    filters: AuditLogFilters = {},
    pageSize = 50,
    lastDocument?: DocumentSnapshot
  ): Promise<PaginatedResult<AuditLog>> {
    this._loading.set(true);

    try {
      const auditCollection = collection(
        this.firestore,
        `tenants/${this.tenantId}/auditLogs`
      );

      const constraints: QueryConstraint[] = [];

      if (filters.actorId) {
        constraints.push(where('actorId', '==', filters.actorId));
      }

      if (filters.resourceType) {
        constraints.push(where('resourceType', '==', filters.resourceType));
      }

      if (filters.resourceId) {
        constraints.push(where('resourceId', '==', filters.resourceId));
      }

      if (filters.action) {
        constraints.push(where('action', '==', filters.action));
      }

      if (filters.startDate) {
        constraints.push(where('createdAt', '>=', filters.startDate));
      }

      if (filters.endDate) {
        constraints.push(where('createdAt', '<=', filters.endDate));
      }

      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(pageSize + 1));

      if (lastDocument) {
        constraints.push(startAfter(lastDocument));
      }

      const q = query(auditCollection, ...constraints);
      const snapshot = await getDocs(q);

      const hasMore = snapshot.docs.length > pageSize;
      const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;

      const items = docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          actorId: data['actorId'],
          actorName: data['actorName'] ?? null,
          actorEmail: data['actorEmail'],
          action: data['action'],
          resourceType: data['resourceType'],
          resourceId: data['resourceId'],
          before: data['before'] ?? null,
          after: data['after'] ?? null,
          ipAddress: data['ipAddress'],
          userAgent: data['userAgent'],
          createdAt: data['createdAt']?.toDate(),
        } as AuditLog;
      });

      return {
        items,
        hasMore,
        lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
      };
    } finally {
      this._loading.set(false);
    }
  }

  async getLogsForResource(
    resourceType: string,
    resourceId: string
  ): Promise<AuditLog[]> {
    const result = await this.getAuditLogs(
      { resourceType, resourceId },
      100
    );
    return result.items;
  }

  async getLogsByActor(actorId: string): Promise<AuditLog[]> {
    const result = await this.getAuditLogs({ actorId }, 100);
    return result.items;
  }
}
